import type { ApolloError } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Modal,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";

import * as graphql from "@/graphql/graphql";
import {
  accountCalendarDateToEndUtc,
  accountCalendarDateToStartUtc,
  addCalendarDays,
} from "@/utils/date-utils";
import { formatGqlExecutionErrors, graphqlClientErrorMessage } from "@/utils/format";

import { CALL_PLAN_SNACKBAR_AUTO_HIDE_MS, callPlanSnackbarOnClose } from "../CallPlans.snackbar";
import { PatientTimezoneDateBlurb } from "../PatientTimezoneDateBlurb";
import { ActivitiesTableWithData, type ActivitiesTableWithDataRef } from "./ActivitiesTable";
import {
  CREATE_CALL_PLAN_MUTATION,
  UPDATE_CALL_PLAN_MUTATION,
} from "./CreateCallPlanModal.mutations";
import { GET_CALL_PLAN_ACTIVITIES_QUERY } from "./CreateCallPlanModal.queries";
import * as sx from "./CreateCallPlanModal.styles";
import * as utils from "./CreateCallPlanModal.utils";
import SelectedActivitiesList from "./SelectedActivitiesList";

type PlanActivityRow = graphql.GetActivitiesQuery["activities"]["activities"][number];
interface CreateCallPlanModalProps {
  accountId: number;
  accountTimeZone?: string | null;
  callPlans: graphql.CallPlanInfoFragment[];
  playlists?: graphql.PlaylistDetailedFragment[];
  activeProgram?: utils.Program | null;
  /** When set, the modal opens in edit mode for this plan. */
  callPlanToEdit?: graphql.CallPlanInfoFragment | null;
  /** After a successful create or update, invoked before closing (e.g. re-open the view modal). */
  onCallPlanCreated?: (plan: graphql.CallPlanInfoFragment) => void;
  open: boolean;
  onClose: () => void;
}

const CreateCallPlanModal: React.FC<CreateCallPlanModalProps> = ({
  accountId,
  accountTimeZone,
  callPlans,
  playlists = [],
  activeProgram,
  callPlanToEdit,
  onCallPlanCreated,
  open,
  onClose,
}) => {
  const [showError, setShowError] = useState(false);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successNotice, setSuccessNotice] = useState<string | null>(null);
  /** Suppress re-opening the error snackbar from the Apollo sync effect until errors clear or the modal closes. */
  const errorSnackbarUserDismissedRef = useRef(false);
  const activitiesListRef = useRef<ActivitiesTableWithDataRef | null>(null);
  const [listQueryErrors, setListQueryErrors] = useState<{
    activitiesError?: ApolloError;
    activityTypesError?: ApolloError;
  }>({});

  const handleListQueryErrors = useCallback(
    (errors: { activitiesError?: ApolloError; activityTypesError?: ApolloError }) => {
      setListQueryErrors(errors);
    },
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    trigger,
    formState: { errors: fieldErrors },
  } = useForm<utils.CallPlanFormValues>({ mode: "onChange" });

  const watchedStartDate = watch("startDate");
  const watchedEndDate = watch("endDate");

  const programWeekDisplay = useMemo(() => {
    if (callPlanToEdit) {
      return callPlanToEdit.programWeek;
    }

    return utils.getNextProgramWeekDisplay(callPlans, activeProgram, playlists);
  }, [callPlanToEdit, callPlans, activeProgram, playlists]);

  const createBlockedByProgramEnd = !callPlanToEdit && !activeProgram;

  const startDateLocked = utils.isCallPlanStartDateLocked(callPlanToEdit);
  const lockedStartDateIso = callPlanToEdit && startDateLocked ? callPlanToEdit.startDate : null;

  const callPlansForOverlap = useMemo(
    () => utils.callPlansForOverlapComparison(callPlans, callPlanToEdit?.id),
    [callPlans, callPlanToEdit?.id]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    reset(
      callPlanToEdit
        ? utils.computeEditFormValues(callPlanToEdit, accountTimeZone)
        : utils.computeDefaults(callPlans, activeProgram, accountTimeZone)
    );
  }, [accountTimeZone, open, callPlans, callPlanToEdit, reset, activeProgram]);

  useEffect(() => {
    if (!open) {
      return;
    }

    trigger(["startDate", "endDate"]);
  }, [open, watchedStartDate, watchedEndDate, callPlansForOverlap, trigger]);

  const [createCallPlan, { loading: createLoading, error: createError }] = useMutation<
    graphql.CreateCallPlanMutation,
    graphql.CreateCallPlanMutationVariables
  >(CREATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });
  const [updateCallPlan, { loading: updateLoading, error: updateError }] = useMutation<
    graphql.UpdateCallPlanMutation,
    graphql.UpdateCallPlanMutationVariables
  >(UPDATE_CALL_PLAN_MUTATION, { refetchQueries: ["GetSingleUser"] });
  const [selectedPlanActivities, setSelectedPlanActivities] = useState<PlanActivityRow[]>([]);

  const selectedPlanActivityIds = useMemo(
    () => Object.fromEntries(selectedPlanActivities.map((a) => [a.id, true])),
    [selectedPlanActivities]
  );

  const { data: callPlanActivitiesData, error: callPlanActivitiesError } = useQuery<
    graphql.GetCallPlanActivitiesQuery,
    graphql.GetCallPlanActivitiesQueryVariables
  >(GET_CALL_PLAN_ACTIVITIES_QUERY, {
    skip: !open || !callPlanToEdit,
    variables: callPlanToEdit ? { callPlanId: callPlanToEdit.id } : undefined,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (!open) {
      setSelectedPlanActivities([]);
      setShowError(false);
      setErrorNotice(null);
      errorSnackbarUserDismissedRef.current = false;
      return;
    }

    const apolloErr =
      createError ??
      updateError ??
      callPlanActivitiesError ??
      listQueryErrors.activitiesError ??
      listQueryErrors.activityTypesError;

    if (!apolloErr) {
      errorSnackbarUserDismissedRef.current = false;
      return;
    }

    if (errorSnackbarUserDismissedRef.current) {
      return;
    }

    const fallback =
      createError ?? updateError
        ? "Could not save the call plan."
        : "Could not load activities or filter options.";
    setErrorNotice(graphqlClientErrorMessage(apolloErr, fallback));
    setShowError(true);
  }, [
    callPlanActivitiesError,
    createError,
    listQueryErrors.activitiesError,
    listQueryErrors.activityTypesError,
    open,
    updateError,
  ]);

  useEffect(() => {
    if (!open || !callPlanToEdit) {
      return;
    }

    const plan = callPlanActivitiesData?.callPlan;
    if (!plan || plan.id !== callPlanToEdit.id) {
      return;
    }

    setSelectedPlanActivities(utils.callPlanInteractionsToPlanActivityRows(plan.interactions));
  }, [open, callPlanToEdit, callPlanActivitiesData]);

  const addActivityToPlan = (activity: PlanActivityRow) => {
    setSelectedPlanActivities((prev) => {
      if (prev.length >= utils.MAX_CALL_PLAN_ACTIVITIES || prev.some((a) => a.id === activity.id)) {
        return prev;
      }

      return [...prev, activity];
    });
  };

  const removeActivityFromPlan = (activityId: number) => {
    setSelectedPlanActivities((prev) => prev.filter((a) => a.id !== activityId));
  };

  const startDateRegistration = register("startDate", {
    required: "Start date is required.",
    validate: (v) =>
      utils.validateStartDate(
        v,
        getValues("endDate"),
        callPlansForOverlap,
        activeProgram,
        startDateLocked,
        accountTimeZone
      ),
  });
  const { onChange: onStartDateChange, ...startDateRegistrationRest } = startDateRegistration;

  const reportCallPlanSaveError = (notice: string) => {
    errorSnackbarUserDismissedRef.current = false;
    setErrorNotice(notice);
    setShowError(true);
  };

  const dismissErrorSnackbar = useCallback(() => {
    errorSnackbarUserDismissedRef.current = true;
    setShowError(false);
  }, []);

  const dismissSuccessSnackbar = useCallback(() => {
    setShowSuccess(false);
    setSuccessNotice(null);
  }, []);

  const submitCreateCallPlan = async (values: utils.CallPlanFormValues) => {
    try {
      const result = await createCallPlan({
        variables: {
          input: {
            accountId,
            activityIds: selectedPlanActivities.map((a) => a.id),
            programWeek: programWeekDisplay,
            startDate: accountCalendarDateToStartUtc(values.startDate, accountTimeZone),
            endDate: accountCalendarDateToEndUtc(values.endDate, accountTimeZone),
          },
        },
      });
      if (!result.data?.createCallPlan) {
        return reportCallPlanSaveError(
          formatGqlExecutionErrors(result) || "Could not create the call plan."
        );
      }

      const createdPlan = result.data.createCallPlan;
      activitiesListRef.current?.resetList();
      setSuccessNotice("Call plan created successfully.");
      setShowSuccess(true);
      onCallPlanCreated?.(createdPlan);
      onClose();
    } catch (e) {
      reportCallPlanSaveError(graphqlClientErrorMessage(e, "Could not create the call plan."));
    }
  };

  const submitUpdateCallPlan = async (values: utils.CallPlanFormValues) => {
    const plan = callPlanToEdit;
    if (!plan) {
      return;
    }
    try {
      const result = await updateCallPlan({
        variables: {
          input: {
            callPlanId: plan.id,
            activityIds: selectedPlanActivities.map((a) => a.id),
            startDate: accountCalendarDateToStartUtc(values.startDate, accountTimeZone),
            endDate: accountCalendarDateToEndUtc(values.endDate, accountTimeZone),
          },
        },
      });
      if (!result.data?.updateCallPlan) {
        return reportCallPlanSaveError(
          formatGqlExecutionErrors(result) || "Could not update the call plan."
        );
      }

      const updatedPlan = result.data.updateCallPlan;
      activitiesListRef.current?.resetList();
      setSuccessNotice("Call plan updated successfully.");
      setShowSuccess(true);
      onCallPlanCreated?.(updatedPlan);
      onClose();
    } catch (e) {
      reportCallPlanSaveError(graphqlClientErrorMessage(e, "Could not update the call plan."));
    }
  };

  if (!open) {
    return null;
  }

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <Box sx={sx.modalContainerSx}>
          <Box sx={sx.modalHeaderSx}>
            <Typography variant="h4" sx={sx.modalTitleSx}>
              {callPlanToEdit ? "Edit Call Plan" : "Create Call Plan"}
            </Typography>
            <IconButton
              onClick={onClose}
              aria-label="Close"
              size="small"
              sx={sx.modalCloseIconButtonSx}
            >
              <CloseIcon sx={sx.modalCloseGlyphSx} />
            </IconButton>
          </Box>

          <Box
            component="form"
            noValidate
            sx={sx.modalFormSx}
            onSubmit={handleSubmit(callPlanToEdit ? submitUpdateCallPlan : submitCreateCallPlan)}
          >
            <PatientTimezoneDateBlurb accountTimeZone={accountTimeZone} sx={{ mb: 1 }} />
            <Box sx={sx.callPlanFieldsSx}>
              <Box sx={sx.callPlanProgramWeekBlockSx}>
                <Typography variant="caption" sx={sx.callPlanProgramWeekCaptionSx}>
                  Program week
                </Typography>
                <Typography variant="body1" sx={sx.callPlanProgramWeekValueSx}>
                  {programWeekDisplay}
                </Typography>
              </Box>
              <Tooltip title={startDateLocked && utils.CALL_PLAN_START_DATE_DELIVERIES_TOOLTIP}>
                <Box component="span" sx={{ display: "inline-block", width: 180 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    disabled={startDateLocked}
                    error={!!fieldErrors.startDate}
                    helperText={fieldErrors.startDate?.message}
                    sx={{ width: "100%" }}
                    {...startDateRegistrationRest}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) {
                        setValue("endDate", addCalendarDays(v, utils.CALL_PLAN_DURATION_DAYS), {
                          shouldValidate: false,
                          shouldDirty: true,
                        });
                      }
                      onStartDateChange(e);
                      if (v) {
                        trigger("endDate");
                      }
                    }}
                  />
                </Box>
              </Tooltip>
              <TextField
                label="End Date"
                type="date"
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!fieldErrors.endDate}
                helperText={fieldErrors.endDate?.message}
                sx={{ width: 180 }}
                {...register("endDate", {
                  required: "End date is required.",
                  validate: (v) =>
                    utils.validateEndDate(
                      v,
                      getValues("startDate"),
                      callPlansForOverlap,
                      activeProgram,
                      lockedStartDateIso,
                      accountTimeZone
                    ),
                })}
              />
            </Box>

            <ActivitiesTableWithData
              ref={activitiesListRef}
              accountId={accountId}
              accountTimeZone={accountTimeZone}
              skip={!open}
              onQueryErrorsChange={handleListQueryErrors}
              mainColumnWrapSx={sx.modalMainColumnSx}
              tableWrapperSx={sx.callPlanModalTableWrapperSx}
              tableScrollAreaSx={sx.callPlanModalTableScrollAreaSx}
              tableContainerSx={sx.callPlanModalTableContainerSx}
              activityIdsSelected={selectedPlanActivityIds}
              atCapacity={selectedPlanActivities.length >= utils.MAX_CALL_PLAN_ACTIVITIES}
              onAddActivity={addActivityToPlan}
              onRemoveActivity={removeActivityFromPlan}
              endSlot={({ queryState, typeKeysSelected }) => (
                <Box sx={sx.selectedActivitiesSectionSx}>
                  <Typography component="h2" sx={sx.callPlanActivitiesHeadingSx}>
                    {`Call plan activities (${selectedPlanActivities.length}/${utils.MAX_CALL_PLAN_ACTIVITIES})`}
                  </Typography>
                  <SelectedActivitiesList
                    activities={selectedPlanActivities}
                    onReorder={setSelectedPlanActivities}
                    onRemove={removeActivityFromPlan}
                    queryState={queryState}
                    typeKeysSelected={typeKeysSelected}
                  />
                </Box>
              )}
            />

            <Box sx={sx.modalFooterBarSx}>
              <Tooltip
                title={
                  createBlockedByProgramEnd && utils.CREATE_CALL_PLAN_DISABLED_PROGRAM_ENDED_MESSAGE
                }
              >
                <span>
                  <Button
                    className="button"
                    variant="contained"
                    type="submit"
                    disabled={
                      createLoading ||
                      updateLoading ||
                      createBlockedByProgramEnd ||
                      selectedPlanActivities.length === 0 ||
                      selectedPlanActivities.length > utils.MAX_CALL_PLAN_ACTIVITIES
                    }
                  >
                    {utils.getCallPlanSubmitButtonLabel({
                      createLoading,
                      updateLoading,
                      callPlanToEdit,
                    })}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>
      </Modal>
      {showError && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showError}
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          onClose={callPlanSnackbarOnClose(dismissErrorSnackbar)}
          TransitionProps={{ onExited: () => setErrorNotice(null) }}
        >
          <Alert onClose={dismissErrorSnackbar} severity="error">
            {errorNotice ?? "Something went wrong."}
          </Alert>
        </Snackbar>
      )}
      {showSuccess && (
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          open={showSuccess}
          autoHideDuration={CALL_PLAN_SNACKBAR_AUTO_HIDE_MS}
          onClose={callPlanSnackbarOnClose(dismissSuccessSnackbar)}
        >
          <Alert onClose={dismissSuccessSnackbar} severity="success">
            {successNotice ?? "Call plan saved."}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default CreateCallPlanModal;
